import React, { useState } from 'react';
import {
    Box,
    Typography,
    Avatar,
    Rating,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useSharedStyles } from '../style';
import { rateDiscussionComment } from '../../../../../../../Utils/apiService';
import { invalidateDiscussionTopicsCache } from '../../../../Helper/DiscussionForumHelper';
import { TopicWithCourse } from '../types';
import relativeTime from 'dayjs/plugin/relativeTime';
import dayjs from 'dayjs';
dayjs.extend(relativeTime);

interface DiscussionRightPanelProps {
    selectedTopic: TopicWithCourse | undefined;
    contactId: string | undefined;
    onCommentRated: () => void;
}

const useStyles = makeStyles(() => ({
    card: {
        backgroundColor: '#fff',
        border: '1px solid #EEF1F5',
        borderRadius: '12px !important',
        boxShadow: '0 8px 24px rgba(31, 65, 114, 0.03)',
        padding: '20px',
        minHeight: '210px',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
    },
    discussionHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '16px',
        gap: '16px',
        flexWrap: 'wrap',
    },
    discussionTitle: {
        fontWeight: 600,
        fontSize: '16px',
        textAlign: 'left',
    },
    headerMeta: {
        fontSize: '13px',
        color: '#8A94A4',
        whiteSpace: 'nowrap',
    },
    replyRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: '16px',
        padding: '18px 0',
        borderBottom: '1px solid #EEF1F5',
        '&:last-child': {
            borderBottom: 'none',
        },
    },
    replyAuthor: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
    },
    authorName: {
        fontWeight: 600,
        textAlign: 'left',
        fontSize: '14px',
    },
    replyMessage: {
        marginTop: '4px',
        color: '#4A5568',
    },
    rateCol: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        flexShrink: 0,
    },
    meritLabel: {
        fontSize: '12px',
        color: '#8A94A4',
        marginTop: '2px',
    },
    emptyState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
    },
    emptyReplies: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        width: '100%',
        minHeight: '120px',
        padding: '20px',
        boxSizing: 'border-box',
        textAlign: 'center',
    },
    emptyRepliesText: {
        fontSize: '18px',
        fontWeight: 500,
        color: '#626262',
    },
}));

const DiscussionRightPanel: React.FC<DiscussionRightPanelProps> = ({
    selectedTopic,
    contactId,
    onCommentRated,
}) => {
    const classes = useStyles();
    const sharedClasses = useSharedStyles();

    const [ratingInProgress, setRatingInProgress] = useState<string | null>(null);

    const handleRate = async (commentId: string, rating: number) => {
        if (!contactId || ratingInProgress) return;
        setRatingInProgress(commentId);
        try {
            await rateDiscussionComment({ contactId, commentId, rating });
            if (selectedTopic?.batchId) invalidateDiscussionTopicsCache(selectedTopic.batchId);
            onCommentRated();
        } catch (error) {
            console.error('Error rating comment:', error);
        } finally {
            setRatingInProgress(null);
        }
    };

    if (!selectedTopic) {
        return (
            <Box className={classes.card}>
                <Box className={classes.emptyState}>
                    <Typography variant="body1" color="textSecondary" gutterBottom align="left">
                        No discussion selected
                    </Typography>
                </Box>
            </Box>
        );
    }

    const total = selectedTopic.comments.length;
    const rated = selectedTopic.comments.filter((c) => c.rating !== undefined && c.rating !== null).length;

    return (
        <Box className={classes.card}>
            <Box className={classes.discussionHeader}>
                <Typography className={`${classes.discussionTitle} ${sharedClasses.font16500}`}>
                    {selectedTopic.question}
                </Typography>
                <Typography className={classes.headerMeta}>
                    {selectedTopic.courseLabel ? `${selectedTopic.courseLabel} · ` : ''}{rated}/{total} rated
                </Typography>
            </Box>

            {total > 0 ? (
                [...selectedTopic.comments].reverse().map((comment) => (
                    <Box key={comment.commentId} className={classes.replyRow}>
                        <Box className={classes.replyAuthor}>
                            <Avatar sx={{ width: 36, height: 36, bgcolor: '#e0e0e0', fontSize: '14px' }}>
                                {comment.commentedByName?.charAt(0) || 'U'}
                            </Avatar>
                            <Box>
                                <Typography className={classes.authorName}>
                                    {comment.commentedByName || 'Unknown User'}
                                </Typography>
                                <Typography className={`${sharedClasses.font14400} ${classes.replyMessage}`}>
                                    {comment.comment}
                                </Typography>
                            </Box>
                        </Box>
                        <Box className={classes.rateCol}>
                            <Rating
                                size="small"
                                value={comment.rating || 0}
                                disabled={ratingInProgress === comment.commentId}
                                onChange={(_e, value) => {
                                    if (value) handleRate(comment.commentId, value);
                                }}
                            />
                            <Typography className={classes.meritLabel}>
                                {comment.rating ? `Merit ${comment.rating}/5` : 'Not yet rated'}
                            </Typography>
                        </Box>
                    </Box>
                ))
            ) : (
                <Box className={classes.emptyReplies}>
                    <Typography className={classes.emptyRepliesText}>No replies yet</Typography>
                </Box>
            )}
        </Box>
    );
};

export default DiscussionRightPanel;
